import React, { useEffect, useRef, useState } from 'react';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import TrashActionIcon from './components/TrashActionIcon';
import ViewActionIcon from './components/ViewActionIcon';
import NoProductImage from "./components/NoProductImage";
import ProductPreviewCard from './ProductPreviewCard';
import SortableTableHeader from './components/SortableTableHeader';
import { getProductMarkets } from './utils/productForm';
import { checkPermissionActions } from './utils/permissions';
import { getAdminAction, getAvailableActions, getWriteActions } from './utils/permissionCatalog';

export default function ProductTable({ products, brands, markets, onEdit, onArchive, toggleSortDirection=null, sort=null, nameSortAscending: _nameSortAscending=null, lastModifiedSortAscending: _lastModifiedSortAscending=null, currentUser=null}) {
  const availableActions = getAvailableActions(currentUser);
  const canEditProducts = checkPermissionActions(availableActions, getWriteActions('Products'));
  const canArchiveProducts = checkPermissionActions(availableActions, getAdminAction('Products'));
  const [openPreviewId, setOpenPreviewId] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const openProduct = openPreviewId
      ? (products || []).find((p) => String(p.id) === String(openPreviewId))
      : null;
  
    const closePreviewAnimated = () => {
      if (!openPreviewId) return;
      if (isClosing) return;
  
      setIsClosing(true);
  
      if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = window.setTimeout(() => {
        setOpenPreviewId(null);
        setIsClosing(false);
        closeTimeoutRef.current = null;
      }, 180);
    };
  
    useEffect(() => {
      return () => {
        if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current);
      };
    }, []);
  
    useEffect(() => {
      if (!openPreviewId) return;
  
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closePreviewAnimated();
      };
  
      window.addEventListener('keydown', onKeyDown);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
      };
    }, [openPreviewId, isClosing]);
  return (
    <>
    <table className="table" data-testid="products-table">
      <thead>
        <tr>
          <th></th>
          {toggleSortDirection ?
          (<SortableTableHeader label="Name" fieldName="name" sort={sort} onSort={toggleSortDirection} />) :
          (
            <th>Name</th>
          )}
          {toggleSortDirection ? <SortableTableHeader label="Brand" fieldName="brandName" sort={sort} onSort={toggleSortDirection} /> : <th>Brand</th>}
          {toggleSortDirection ? <SortableTableHeader label="Markets" fieldName="marketName" sort={sort} onSort={toggleSortDirection} /> : <th>Markets</th>}
          {toggleSortDirection ? <SortableTableHeader label="Category" fieldName="productCategoryName" sort={sort} onSort={toggleSortDirection} /> : <th>Category</th>}
          {toggleSortDirection ? <SortableTableHeader label="Strain" fieldName="strain" sort={sort} onSort={toggleSortDirection} /> : <th>Strain</th>}
          {toggleSortDirection ? <SortableTableHeader label="Status" fieldName="status" sort={sort} onSort={toggleSortDirection} /> : <th>Status</th>}
          {toggleSortDirection ? <SortableTableHeader label="UPC" fieldName="inventoryUpc" sort={sort} onSort={toggleSortDirection} /> : <th>UPC</th>}
          {toggleSortDirection ?
          (<SortableTableHeader label="Last Updated" fieldName="lastModified" sort={sort} onSort={toggleSortDirection} />) :
          (
            <th>Last Updated</th>
          )}
          <th className="col-actions">Actions</th>
        </tr>
      </thead>
      <tbody>
        {products.length === 0 && (
          <tr data-testid="products-table-empty">
            <td colSpan={10}>No products found.</td>
          </tr>
        )}
        {products.map((p) => (
          <tr key={p.id} data-testid={`product-row-${p.id}`}>
            <td width="56">
              {
                p.productImageUrl ?
                  <div className="thumb" style={{ backgroundImage: `url(${p.productImageUrl || ''})` }} aria-hidden />
                  : <NoProductImage />
              }
            </td>
                <td>{p.name}</td>
                <td>{brands.find(b => b.id == p.brandId)?.name || ''}</td>
                <td>{getProductMarkets(p).map((marketName, index) => (
                  <span key={`${p.id}-${marketName}-${index}`} className="tag" style={{ margin: '4px' }}>{marketName}</span>
                ))}</td>
                <td>{p.productCategoryName}</td>
            <td>{p.strain || '—'}</td>
            <td className={p.isArchived ? 'status-archived' : 'status-active'}>{p.isArchived ? 'Archived' : 'Active'}</td>

            <td>{p.inventoryUpc || '—'}</td>
            <td>{p.lastModified || '—'}</td>
            <td className="col-actions">
              <div className="table-actions">
                <IconButton
                  title="View"
                  ariaLabel={`View ${p.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsClosing(false);
                    setOpenPreviewId(String(p.id));
                  }}
                  testId={`product-view-${p.id}`}
                >
                  <ViewActionIcon />
                </IconButton>
                {canEditProducts && <IconButton title="Edit" ariaLabel={`Edit ${p.name}`} onClick={() => onEdit && onEdit(p)} testId={`product-edit-${p.id}`}>
                  <EditActionIcon />
                </IconButton>}
                {canArchiveProducts && <IconButton
                  title="Archive"
                  ariaLabel={`Archive ${p.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onArchive) onArchive(p);
                  }}
                  testId={`product-archive-${p.id}`}
                >
                  <TrashActionIcon />
                </IconButton>}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>

    {openProduct ? (
            <div
              className="modal-backdrop product-preview-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label={`Preview ${openProduct.name || 'product'}`}
              data-testid={`product-preview-modal-${openProduct.id}`}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closePreviewAnimated();
              }}
            >
              <div
                className={`modal product-preview-drawer page-slide-in${isClosing ? ' page-slide-out' : ''}`}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h2 className="modal-title">Product Preview</h2>
                  <button className="subtle-btn" onClick={closePreviewAnimated} data-testid={`product-preview-close-${openProduct.id}`}>Close</button>
                </div>
    
                <div className="product-preview-drawer-body">
                  <ProductPreviewCard
                    id={openProduct.id}
                    name={openProduct.name}
                    brand={ brands.find(b => b.id === openProduct.brandId)?.name || '' }
                    category={openProduct.productCategoryName || ''}
                    strain={openProduct.strain}
                    inventoryUpc={openProduct.inventoryUpc}
                    imageName={openProduct.imageName || (typeof openProduct.image === 'string' ? openProduct.image.split('/').pop() : '') || ''}
                    imageUrl={typeof openProduct.productImageUrl === 'string' ? openProduct.productImageUrl : ''}
                    markets={ markets.filter(m => openProduct.marketIds.includes(m.id)).map(m => m.name || m.label || m.abbreviation || String(m.id)) }
                    description={openProduct.description}
                    isArchived={Boolean(openProduct.isArchived)}
                    archivedAt={openProduct.archivedAt}
                    showStatusBadge
                    emptyNameLabel="—"
                    container="card"
                  />
                </div>
              </div>
            </div>
          ) : null}
          </>
  );
}
